import { Seo } from '@/components/Seo';
import { JsonLd, makeItemListSchema, makeFAQSchema, makeBreadcrumbSchema } from '@/components/Schema';
import { FAQ } from '@/components/FAQ';
import { FinalCTA } from '@/components/FinalCTA';
import { TemplateGallery } from '@/components/TemplateGallery';
import { Link } from 'react-router-dom';
import { TEMPLATES } from '@/types';

const faqs = [
  {
    question: 'Are the templates really free?',
    answer: 'Classic and Student templates are free. Minimal, Modern Clean, and Career Change are available with Pro.',
  },
  {
    question: 'Can I switch templates after I fill in my details?',
    answer: 'Yes. You can preview your resume in any available template before downloading.',
  },
  {
    question: 'Will these templates work with ATS?',
    answer: 'Yes. Every template is single-column and uses ATS-safe fonts and headings.',
  },
  {
    question: 'Do I need to sign up to use a template?',
    answer: 'No. Pick a template, fill in your details, and download your PDF. No account required.',
  },
  {
    question: 'What is the difference between free and Pro templates?',
    answer: 'Free templates cover the most common layouts. Pro templates add more design options while staying ATS-friendly.',
  },
];

export function ResumeTemplates() {
  const items = TEMPLATES.map((t) => ({
    name: t.name,
    description: t.description,
    url: `https://ai-free-resume.org/resume-templates#${t.id}`,
  }));

  return (
    <>
      <Seo
        title="Free Resume Templates | ATS-Friendly, Instant Download"
        description="Choose a clean, ATS-friendly resume template and start building for free. No sign-up required."
        path="/resume-templates"
      />
      <JsonLd schema={makeItemListSchema(items)} />
      <JsonLd schema={makeFAQSchema(faqs)} />
      <JsonLd schema={makeBreadcrumbSchema([{ name: 'Resume Templates', path: '/resume-templates' }])} />

      <section className="hero-glow py-24 md:py-32">
        <div className="mx-auto max-w-[800px] px-6 text-center">
          <h1 className="text-balance text-4xl font-extrabold text-white md:text-5xl">
            Free Resume Templates — ATS-Friendly & Professional
          </h1>
          <p className="mx-auto mt-6 max-w-[680px] text-lg text-white/75">
            Choose a clean, ATS-friendly layout and start building your resume now. All templates are designed for easy reading and parsing.
          </p>
          <Link
            to="/app/create"
            className="btn-primary mt-8 inline-flex items-center justify-center rounded-[10px] px-8 py-4 text-base font-semibold"
          >
            Start with a Free Template
          </Link>
        </div>
      </section>

      <section className="bg-[#F6F9FC] py-20 md:py-28">
        <div className="mx-auto max-w-[1200px] px-6">
          <div className="text-center">
            <span className="rounded-full bg-[#E7F1FA] px-4 py-1.5 text-sm font-semibold text-[#0A2540]">
              All templates
            </span>
            <h2 className="mt-4 text-3xl font-bold text-[#0A2540] md:text-4xl">
              Pick the look that fits your story
            </h2>
          </div>
          <div className="mt-12">
            <TemplateGallery linkTo="/app/create" featuredId="modern-clean" />
          </div>
        </div>
      </section>

      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-[1200px] px-6">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-[#0A2540] md:text-4xl">
              How to choose a template
            </h2>
          </div>
          <div className="mt-12 grid gap-8 md:grid-cols-3">
            {[
              { title: 'Keep it simple', desc: 'When in doubt, choose a clean layout. ATS parsers and recruiters both prefer simplicity.' },
              { title: 'Match your experience', desc: 'Students often do well with the Student template. Professionals can use Classic or Modern Clean.' },
              { title: 'Highlight transferable skills', desc: 'Career changers should pick a layout that emphasizes skills and achievements over chronology.' },
            ].map((t) => (
              <div key={t.title} className="rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-6 transition duration-300 hover:bg-white hover:shadow-sm">
                <h3 className="text-xl font-semibold text-[#0A2540]">{t.title}</h3>
                <p className="mt-2 text-[#4A5568]">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#F6F9FC] py-16">
        <div className="mx-auto max-w-[800px] px-6">
          <div className="text-center">
            <span className="rounded-full bg-[#E7F1FA] px-4 py-1.5 text-sm font-semibold text-[#0A2540]">
              FAQ
            </span>
            <h2 className="mt-4 text-3xl font-bold text-[#0A2540]">
              Template FAQ
            </h2>
          </div>
          <div className="mt-10">
            <FAQ items={faqs} />
          </div>
        </div>
      </section>

      <FinalCTA
        title="Build your free resume now"
        subtitle="No sign-up. No credit card. Download in seconds."
        buttonText="Create My Resume"
        buttonTo="/app/create"
      />
    </>
  );
}

export default ResumeTemplates;
